/**
 * Product links API client (backend/routes/productLinks.js,
 * docs/memory/product-links.md): Shopify Search & Discovery "Related" and
 * "Complementary" lists, mirrored in Firestore. Same shape as the kit splits
 * client: axios + getApiBaseUrl + credentials, data unwrapped. Plus the pure
 * index helpers the Products grid uses to find a card's links without a
 * request per card.
 */

import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiClient';
import { isCccProduct, isPhaserProduct } from '../../utils/productStockEdit';

const BASE = () => `${getApiBaseUrl()}/api/product-links`;
const opts = { withCredentials: true };

/** Shopify's cap per list. */
export const PRODUCT_LINKS_MAX = 10;
const MIN_LOOKUP_LENGTH = 2;

/**
 * related_products_display. '' = unset, which Shopify treats as 'ahead'
 * (manual picks ahead of automatic recommendations).
 */
export const RELATED_DISPLAY_OPTIONS = [
  { value: '', label: 'Manual picks ahead of automatic' },
  { value: 'only manual', label: 'Only manual picks' }
];

export const skuUpper = (sku) => String(sku || '').trim().toUpperCase();

/** Firestore id shape: `${store}__${productLegacyId}`; the API's own id wins. */
export function linkIdFor(link) {
  if (!link) return null;
  if (link.id) return link.id;
  if (!link.store || !link.productLegacyId) return null;
  return `${link.store}__${link.productLegacyId}`;
}

/** 'phaser' | 'ccc' | null, from a link's storeKey or a product/store domain. */
export function linkStoreKeyFor({ link, product, store } = {}) {
  if (link?.storeKey) return link.storeKey;
  const probe = product || { store: store || link?.store };
  if (isPhaserProduct(probe)) return 'phaser';
  if (isCccProduct(probe)) return 'ccc';
  return null;
}

/** A related entry a kit split added: locked in the links editor. */
export const isKitEntry = (entry) => entry?.source === 'kit';

/** "3372" from a kitId `${store}__3372`. */
export function kitSkuFromId(kitId) {
  const id = String(kitId || '');
  const at = id.lastIndexOf('__');
  return at >= 0 ? id.slice(at + 2) : id;
}

/** GET /api/product-links → links[] (both stores). */
export async function fetchProductLinks() {
  const res = await axios.get(BASE(), opts);
  return Array.isArray(res.data?.links) ? res.data.links : [];
}

/**
 * PUT /api/product-links with { store, sku, related: [sku], complementary: [sku], relatedDisplay? }
 * (the full MANUAL lists; kit entries are kept server-side)
 * → { link, affected, removed, warnings }. relatedDisplay is sent only when set.
 */
export async function saveProductLinks({ store, sku, related, complementary, relatedDisplay } = {}) {
  const payload = {
    store,
    sku,
    related: Array.isArray(related) ? related : [],
    complementary: Array.isArray(complementary) ? complementary : []
  };
  if (relatedDisplay) payload.relatedDisplay = relatedDisplay;
  const res = await axios.put(BASE(), payload, opts);
  return {
    link: res.data?.link || null,
    affected: Array.isArray(res.data?.affected) ? res.data.affected : [],
    removed: Array.isArray(res.data?.removed) ? res.data.removed : [],
    warnings: Array.isArray(res.data?.warnings) ? res.data.warnings : []
  };
}

/** GET /api/product-links/lookup?q=&store= → results[] (same picker shape as kit splits). */
export async function lookupLinkProducts(q, store) {
  const query = String(q || '').trim();
  if (query.length < MIN_LOOKUP_LENGTH) return [];
  const res = await axios.get(`${BASE()}/lookup`, {
    ...opts,
    params: { q: query, store: store || undefined }
  });
  return Array.isArray(res.data?.results) ? res.data.results : [];
}

/** Server `{ message | error }` text, else the transport message. */
export function readProductLinksError(err, fallback = 'Request failed') {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
}

const indexKey = (store, sku) => `${store}__${skuUpper(sku)}`;

/**
 * Map keyed `${store}__${SKU_UPPER}` → { link: Record|null, complementedBy: Entry-like[] }.
 *
 * Each mirror doc fans out over its skus[] (the lists are product-level, so
 * every variant SKU of a product shares ONE entry object). complementedBy is
 * derived: the other docs whose complementary list names this product's
 * productId. A product that is only complemented (no doc of its own) is keyed
 * by the SKU the naming doc stored for it. Entries are built once per links
 * array, so a card's productLink keeps its identity (the ProductCard memo
 * compares it by reference) until the links change.
 */
export function buildProductLinkIndex(links) {
  const index = new Map();
  const docs = (Array.isArray(links) ? links : []).filter((l) => l && l.store);
  const byProduct = new Map(); // `${store}__${productId}` → entry

  const entryForProduct = (store, productId) => {
    const key = `${store}__${productId}`;
    let entry = byProduct.get(key);
    if (!entry) {
      entry = { link: null, complementedBy: [] };
      byProduct.set(key, entry);
    }
    return entry;
  };
  const fanOut = (store, sku, entry) => {
    if (!sku) return;
    const key = indexKey(store, sku);
    if (!index.has(key)) index.set(key, entry);
  };

  docs.forEach((doc) => {
    const entry = doc.productId ? entryForProduct(doc.store, doc.productId) : { link: null, complementedBy: [] };
    entry.link = doc;
    const skus = Array.isArray(doc.skus) && doc.skus.length ? doc.skus : [doc.primarySku].filter(Boolean);
    skus.forEach((sku) => fanOut(doc.store, sku, entry));
  });

  docs.forEach((doc) => {
    (Array.isArray(doc.complementary) ? doc.complementary : []).forEach((target) => {
      if (!target?.productId || target.productId === doc.productId) return;
      const entry = entryForProduct(doc.store, target.productId);
      if (!entry.link) fanOut(doc.store, target.sku, entry);
      if (doc.productId && entry.complementedBy.some((c) => c.productId === doc.productId)) return;
      const fromSku = doc.primarySku || (Array.isArray(doc.skus) ? doc.skus[0] : '') || '';
      entry.complementedBy.push({
        productId: doc.productId || null,
        sku: fromSku,
        title: doc.title || '',
        handle: doc.handle || '',
        imageUrl: doc.imageUrl || null,
        fromSku,
        fromTitle: doc.title || ''
      });
    });
  });

  return index;
}

/** The product's entry in the index, or null when nothing links it. */
export function productLinkFor(index, product) {
  if (!index || !product?.store || !product.sku || product.sku === 'N/A') return null;
  return index.get(indexKey(product.store, product.sku)) || null;
}

/**
 * A new links array: a save's `affected` docs replace the old ones by id (or
 * join the end when new) and the `removed` ids are dropped. Inputs are not
 * mutated; untouched docs keep their identity.
 */
export function mergeAffected(links, affected, removed) {
  const removedIds = new Set((Array.isArray(removed) ? removed : []).filter(Boolean));
  const incoming = new Map();
  (Array.isArray(affected) ? affected : []).forEach((doc) => {
    const id = linkIdFor(doc);
    if (id && !removedIds.has(id)) incoming.set(id, doc);
  });
  const out = [];
  const placed = new Set();
  (Array.isArray(links) ? links : []).forEach((doc) => {
    const id = linkIdFor(doc);
    if (id && removedIds.has(id)) return;
    if (id && incoming.has(id)) {
      if (!placed.has(id)) out.push(incoming.get(id));
      placed.add(id);
      return;
    }
    out.push(doc);
  });
  incoming.forEach((doc, id) => {
    if (!placed.has(id)) out.push(doc);
  });
  return out;
}
